import {
  Alert,
  Button,
  Drawer,
  Empty,
  Flex,
  Input,
  LegacyForm,
  Page,
  Skeleton,
  Space,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { DashboardRoutes } from "@/router/constant";
import { Add, Close } from "@payping/icons";
import { SubLayout } from "@/components/SubLayout";
import classNames from "classnames";
import styles from "@/assets/scss/Settlement/form.module.scss";
import { BankFormType } from "@/pages/Settings/Bank/types";
import { MerchantIbanStatusEnum } from "@payping/api/app/api/terminalmanagerTypes";
import { useAppSelector } from "@/utils";
import { useIbans } from "@/pages/Settings/Bank/hooks/useIbans";
import { sdkFetch, terminalManagerApis } from "@payping/api";
import ShebaCard from "./components/ShebaCard";

const BankEdit: FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = LegacyForm.useForm<BankFormType>();
  const [isEditing, setIsEditing] = useState(false);
  const { loading: loadingIbans, retry: retryGetIbans } = useIbans();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const businessSettingIban = useAppSelector((state) => state.withdraws.businessSetting.iban);
  const ibanList = useAppSelector((state) => state.terminal.ibans.list?.items);
  const activeIban = LegacyForm.useWatch("activeIban", form);

  const fillForm = useCallback(() => {
    form.setFieldsValue({
      activeIban: ibanList?.find((item) => item.iban === businessSettingIban)?.iban,
      ibanItems: (ibanList || []).map((item) => ({
        id: item.iban,
        title: item.description,
        iban: item.iban?.replace("IR", ""),
        status: item.status,
        errorMessage: item.requestErrors
          ?.map((error: any) => `${error.fieldName}: ${error.codeExceptionTitle}`)
          .join(", "),
      })),
    });
  }, [businessSettingIban, form, ibanList]);

  useEffect(() => {
    fillForm();
  }, [fillForm]);

  const acceptedCount = (ibanList || []).filter((item) => item.status === MerchantIbanStatusEnum.Accepted).length;
  const pendingCount = (ibanList || []).filter((item) => item.status === MerchantIbanStatusEnum.Pending).length;
  const rejectedCount = (ibanList || []).filter((item) => item.status === MerchantIbanStatusEnum.Failed).length;

  const [{ loading: loadingIbanSubmit }, handleBusinessSettingSubmit] = useMessagedAsyncFn(
    async (iban?: string) => {
      if (iban) {
        await sdkFetch("/v1/businessSetting/iban", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Iban: iban }),
        });
      }
    },
    [],
    undefined,
    { useProgress: true },
  );
  const [{ loading: loadingSubmit }, handleSubmit] = useMessagedAsyncFn(
    async (values: BankFormType) => {
      setIsEditing(false);
      try {
        const formattedIbans = values.ibanItems?.map((item) => ({
          iban: `IR${item.iban}`,
          description: item.title,
        }));
        const addedIbans =
          formattedIbans?.filter((formItem) => ibanList?.every((initialItem) => formItem.iban !== initialItem.iban)) ||
          [];
        const removedIbans =
          ibanList?.filter((initialItem) => formattedIbans?.every((formItem) => formItem.iban !== initialItem.iban)) ||
          [];
        const modifiedIbans =
          formattedIbans?.filter((formItem) =>
            ibanList?.some(
              (initialItem) => formItem.iban === initialItem.iban && formItem.description !== initialItem.description,
            ),
          ) || [];
        const hasIbansChanged = addedIbans.length > 0 || removedIbans.length > 0 || modifiedIbans.length > 0;
        if (hasIbansChanged) {
          await terminalManagerApis.updateShaparakIbans({
            addedIbans: [...addedIbans, ...modifiedIbans],
            removedIbans: [...removedIbans, ...modifiedIbans],
          });
          retryGetIbans();
        }
      } catch {
        /* empty */
      }
      if (businessSettingIban !== values.activeIban) {
        const fallbackIban = values.ibanItems?.length === 1 ? `IR${values.ibanItems[0].iban}` : undefined;
        await handleBusinessSettingSubmit(values.activeIban || fallbackIban);
      }
      retryGetIbans();
    },
    [businessSettingIban, retryGetIbans, ibanList, handleBusinessSettingSubmit],
    undefined,
    { useProgress: true },
  );

  const loading = loadingIbans || loadingSubmit || loadingIbanSubmit;

  return (
    <>
      <Drawer
        title={t("profile.bank.editBankInfo")}
        open={true}
        size="100%"
        placement="bottom"
        closeIcon={false}
        extra={
          <Button
            type="text"
            icon={<Close width={14} height={14} />}
            onClick={() =>
              !activeIban
                ? navigate(DashboardRoutes.home.overview.url)
                : navigate(DashboardRoutes.settings.bank.root.url)
            }
          />
        }
      >
        <Page>
          <SubLayout>
            <Flex vertical gap="24px" className="tw:w-full tw:md:mx-auto tw:md:w-1/2">
              {!activeIban && (
                <Alert
                  title={t("profile.bank.noActiveIbanMessage")}
                  type="warning"
                  showIcon
                  className="tw:mb-4!"
                  closable
                  onClose={() => {
                    searchParams.delete("noActiveIban");
                    setSearchParams(searchParams);
                  }}
                />
              )}
              {pendingCount > 0 && (
                <Alert
                  title={t("profile.bank.pendingRequestWarningWithShaparak")}
                  type="warning"
                  className="tw:mb-4!"
                />
              )}

              <div className="tw:text-sm tw:leading-relaxed">
                <ul className="tw:list-disc tw:space-x-0 tw:space-y-2 tw:px-0! tw:py-0!">
                  <li className="tw:flex tw:items-start">
                    <span className="tw:bg-gray-400 tw:ml-2! tw:mt-2! tw:inline-block tw:h-1 tw:w-1 tw:shrink-0 tw:rounded-full"></span>
                    <Typography.Text type="secondary">{t("profile.bank.info1")}</Typography.Text>
                  </li>
                  <li className="tw:flex tw:items-start">
                    <span className="tw:bg-gray-400 tw:ml-2! tw:mt-2! tw:inline-block tw:h-1 tw:w-1 tw:shrink-0 tw:rounded-full"></span>
                    <Typography.Text type="secondary">{t("profile.bank.info2")}</Typography.Text>
                  </li>
                  <li className="tw:flex tw:items-start">
                    <span className="tw:bg-gray-400 tw:ml-2! tw:mt-2! tw:inline-block tw:h-1 tw:w-1 tw:shrink-0 tw:rounded-full"></span>
                    <Typography.Text type="secondary">{t("profile.bank.info3")}</Typography.Text>
                  </li>
                </ul>
              </div>

              {ibanList && ibanList.length < 2 && isEditing ? (
                <Alert title={t("profile.bank.shaparakTwoIbanWarning")} type="info" showIcon className="tw:mb-4!" />
              ) : null}

              {loading ? (
                <Skeleton active paragraph />
              ) : (
                <LegacyForm<BankFormType>
                  className={classNames(styles.form)}
                  onFinish={handleSubmit}
                  layout={lg ? "inline" : "vertical"}
                  form={form}
                >
                  <LegacyForm.Item hidden name="activeIban">
                    <Input />
                  </LegacyForm.Item>
                  <LegacyForm.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => (
                      <LegacyForm.List
                        name="ibanItems"
                        rules={[
                          {
                            validator: async (_, items) => {
                              if (!items || items.length < 2) {
                                return Promise.reject(new Error(t("profile.bank.atLeastTwoIbanRequired")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        {(fields, { add, remove }, { errors }) => (
                          <>
                            <Flex justify="space-between" align="center" className="tw:w-full">
                              <Typography.Text className="tw:text-base">{t("profile.bank.accounts")}</Typography.Text>
                              {isEditing ? (
                                <Flex gap={"small"}>
                                  <Button
                                    type={"default"}
                                    onClick={() => {
                                      // retryGetIbans();
                                      fillForm();
                                      setIsEditing(false);
                                    }}
                                  >
                                    {t("common.cancel")}
                                  </Button>
                                  <Button htmlType={"submit"} type={"primary"}>
                                    {t("profile.bank.saveAndConfirm")}
                                  </Button>
                                </Flex>
                              ) : (
                                <Button
                                  type={"primary"}
                                  onClick={() => {
                                    setIsEditing(true);
                                    const currentItems = getFieldValue("ibanItems") || [];
                                    const itemsToAdd = Math.max(0, 2 - currentItems.length);
                                    for (let i = 0; i < itemsToAdd; i += 1) {
                                      add();
                                    }
                                  }}
                                >
                                  {t("common.edit")}
                                </Button>
                              )}
                            </Flex>

                            {errors && errors.length > 0 ? (
                              <LegacyForm.Item>
                                <LegacyForm.ErrorList errors={errors} />
                              </LegacyForm.Item>
                            ) : null}
                            {fields.length === 0 ? null : (
                              <Space orientation="vertical" className="tw:mt-4! tw:w-full" size="middle">
                                {fields?.map((fieldData) => {
                                  const item = form.getFieldValue("ibanItems")?.[fieldData.name];
                                  const displayIban = `IR${item?.iban}`;

                                  return (
                                    <ShebaCard
                                      iban={displayIban}
                                      status={item?.status}
                                      title={item?.title}
                                      errorMessage={item?.errorMessage}
                                      onDelete={() => remove(fieldData.name)}
                                      isActiveIBAN={displayIban === activeIban || fields.length === 1}
                                      isEditing={isEditing}
                                      fieldData={fieldData}
                                      key={fieldData.key}
                                      onActivate={async (iban) => {
                                        if (!isEditing) {
                                          await handleBusinessSettingSubmit(iban);
                                          retryGetIbans();
                                        } else {
                                          form.setFieldsValue({ activeIban: iban });
                                        }
                                      }}
                                      hasPending={pendingCount > 0}
                                    />
                                  );
                                })}
                              </Space>
                            )}

                            {isEditing ? (
                              <Button
                                className="tw:mt-4!"
                                block
                                onClick={() => add()}
                                icon={<Add width={14} height={14} />}
                                disabled={pendingCount > 0}
                              >
                                {t("profile.bank.addNewIBAN")}
                              </Button>
                            ) : null}
                          </>
                        )}
                      </LegacyForm.List>
                    )}
                  </LegacyForm.Item>
                </LegacyForm>
              )}
              {!loading && !isEditing && acceptedCount === 0 && pendingCount === 0 && rejectedCount === 0 ? (
                <Empty description={t("profile.noSheba")} />
              ) : null}
            </Flex>
          </SubLayout>
        </Page>
      </Drawer>
    </>
  );
};

export default BankEdit;
